import { links } from '@/services/dictionaries/links';
import { useSecurity } from '@/services/hooks/useSecurity';
import { debounce } from '@/services/tools/debounce';
import { UpdateDialog } from '@/store/dialog/actions';
import {
    BasicInput,
    InfoPointIcon,
    palette,
    SimplePopover,
    SimplePopoverPlainOption,
    Typography,
} from '@dev/ee-components';
import { Link } from '@material-ui/core';
import { capitalize } from 'lodash-es';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { AdditionalDescWrapper, AdditionalValueLength, AlertWrapper, SimplePopoverWrapper } from './styles';
import { SpecialFieldsModalProps } from './types';
import { SpecialField } from '../../../../services/hooks/useEmailTemplateSpecialFields/types';
import { useSpecialFields } from '../../hooks/useSpecialFields';

const KEYWORD_SEPARATOR = ':';

const getSpecialField = (specialFields: SpecialField[], name: string) =>
    specialFields.find((field): boolean => field.name === name);

const getAdditionalValue = (keyword: string, fullValue?: string): string =>
    keyword ? fullValue?.slice(keyword.length + 1) : '';
const getFullValue = (keyword?: string, additionalValue?: string): string =>
    keyword ? `${keyword}${additionalValue ? KEYWORD_SEPARATOR : ''}${additionalValue}` : '';

export const SpecialFieldsModal = ({
    onChange,
    defaultField,
    isError,
    isButton,
}: SpecialFieldsModalProps): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const specialFields = useSpecialFields({ isButton });
    const [specialField, setSpecialField] = useState<SpecialField>(
        getSpecialField(specialFields.fields, defaultField?.value)
    );
    const [additionalValue, setAdditionalValue] = useState<string>(
        getAdditionalValue?.(specialField?.regexKeyword, defaultField?.fullValue)
    );
    const { isReseller } = useSecurity();

    const options = specialFields.fields.map(
        (field): SimplePopoverPlainOption => ({
            label: capitalize(field.name),
            value: field.name,
        })
    );

    const changeAdditionalValueHandler = (ev: React.ChangeEvent<HTMLInputElement>) => {
        const additionalValue = ev.currentTarget.value;
        setAdditionalValue(additionalValue);
        const textValue = specialField.getTextValueToInsert?.(additionalValue);
        const fullValue = getFullValue(specialField?.regexKeyword, additionalValue);
        const debounceValue = specialField?.isAdditionalValueRequired ? 600 : 0; //if isAdditionalValueRequired = false, save btn is active so we need immediately set isValid
        debounce((): void => {
            onChange({
                value: specialField?.name,
                fullValue,
                textValue: specialField.getTextValueToInsert?.(additionalValue),
                isValid: specialField?.regexFormat?.test(textValue ?? fullValue),
                isAdditonalValueEmptyButRequired:
                    specialField?.isAdditionalValueRequired && additionalValue.length === 0,
            });
        }, debounceValue);
    };

    useEffect(() => {
        if (
            !defaultField.value ||
            (defaultField.value && specialField?.isAdditionalValueRequired && !additionalValue)
        ) {
            dispatch(
                UpdateDialog({
                    isDisabled: true,
                })
            );
        }
    }, []);

    const onItemClick = (event, option: SimplePopoverPlainOption): void => {
        const name = option.value as string;
        const specialField = getSpecialField(specialFields.fields, name);
        const fullValue = getFullValue(specialField?.regexKeyword, '');

        if (!specialField) {
            return;
        }
        setSpecialField(specialField);
        onChange({
            value: specialField.name,
            fullValue: specialField.exactFormat ?? fullValue,
            isValid: true,
            isAdditonalValueEmptyButRequired: specialField?.isAdditionalValueRequired && additionalValue.length === 0,
        });
        setAdditionalValue('');
    };

    return (
        <>
            <AlertWrapper>
                <InfoPointIcon />
                <Typography variant="body1">
                    {t('selectmergefieldinfo')}{' '}
                    {!isReseller && (
                        <Link target="_blank" style={{ color: palette.indigo[6] }} href={links.mergeTags}>
                            {t('learnmore')}
                        </Link>
                    )}
                </Typography>
            </AlertWrapper>
            <SimplePopoverWrapper>
                <SimplePopover
                    value={specialField?.name}
                    label={t('mergefield')}
                    fullWidth
                    defaultPlaceholder={t('select')}
                    listItemWidth="535px"
                    type={'input'}
                    maxHeight={400}
                    placement="bottom"
                    onItemClick={onItemClick}
                    options={options}
                    disableSentenceCase
                />
            </SimplePopoverWrapper>

            {specialField?.regexFormat && (
                <>
                    {specialField?.additionalValueDescription && (
                        <AdditionalDescWrapper>
                            <Typography variant="body1">{specialField?.additionalValueDescription}</Typography>
                        </AdditionalDescWrapper>
                    )}
                    <BasicInput
                        id="additional-input"
                        value={additionalValue}
                        label={specialField?.additionalValueTitle}
                        placeholder={specialField?.additionalValuePlaceholder}
                        onChange={changeAdditionalValueHandler}
                        error={isError}
                        errorMessage={specialField?.additionalValueError}
                        required={!!specialField?.requiredText}
                        requiredText={specialField?.requiredText}
                        disableSentenceCase
                    />
                    {!!specialField?.additionalValueMaxLength && (
                        <AdditionalValueLength>
                            {additionalValue.length}/{specialField?.additionalValueMaxLength}
                        </AdditionalValueLength>
                    )}
                </>
            )}
        </>
    );
};
